import { useState } from 'react';

import { createPortal } from 'react-dom';

import { useAppState } from '../../AppStateProvider';
import { NT_PURCHASE_PAGE_LINK } from '../../domain/gainAccessView';
import type { UserHasRequestedAccessSettingsKey } from '../../domain/user';
import { useNTStatusQuery } from '../../features/user/useNTStatusQuery';
import type { Feature } from '../../features/user/useRequestAccessMutation';
import { useRequestAccessMutation } from '../../features/user/useRequestAccessMutation';
import { ContactUsSuccessModal } from '../../pages/settings/ContactUsSuccessModal/ContactUsSuccessModal';
import { appStore } from '../../services/appStore';
import { translate } from '../../services/i18n';
import { Modal } from '../Modal';
import type { TooltipProps } from '../Tooltip/Tooltip';
import { Tooltip } from '../Tooltip/Tooltip';

import { StyledTooltipLink, StyledTooltipButton } from './styles';

type TooltipWithContactUsProps = {
  settingKey: UserHasRequestedAccessSettingsKey;
  featureKey: Feature;
  onContactUsClick?: () => void;
  text: string;
  accessRequestedText: string;
} & Omit<TooltipProps, 'content'>;

export const TooltipWithContactUs = ({
  settingKey,
  featureKey,
  onContactUsClick,
  text,
  accessRequestedText,
  children,
  ...rest
}: TooltipWithContactUsProps) => {
  const { hasRequestedAccess } = useAppState();
  const hasAlreadyRequested = Boolean(hasRequestedAccess?.[settingKey]);

  const { data: NTStatus } = useNTStatusQuery();
  const [isHookModalOpen, setIsHookModalOpen] = useState(false);

  const requestAccessMutation = useRequestAccessMutation();

  const handleClick = () => {
    requestAccessMutation.mutate(featureKey);
    setIsHookModalOpen(true);
    appStore.setHasRequestedAccess({ [settingKey]: true });
    onContactUsClick?.();
  };

  const renderTooltipContent = () => {
    if (NTStatus?.isAllowed) {
      return (
        <>
          {text}
          <StyledTooltipLink href={NT_PURCHASE_PAGE_LINK} target="_blank" rel="noreferrer" onClick={onContactUsClick}>
            {translate('upgrade.button')}
          </StyledTooltipLink>
        </>
      );
    }

    return hasAlreadyRequested ? (
      accessRequestedText
    ) : (
      <>
        {text}
        <StyledTooltipButton onClick={handleClick}>{translate('common.contact_us')}</StyledTooltipButton>
      </>
    );
  };

  return (
    <>
      <Tooltip {...rest} enabled={isHookModalOpen ? false : rest.visible} content={<div>{renderTooltipContent()}</div>}>
        {children}
      </Tooltip>
      {createPortal(
        <Modal isOpen={isHookModalOpen} onClose={() => setIsHookModalOpen(false)}>
          <ContactUsSuccessModal />
        </Modal>,
        document.body
      )}
    </>
  );
};
